import React, { useState, useEffect, useCallback } from 'react';
import { FieldLabel } from './FieldLabel';
import { useNavigate } from 'react-router-dom';
import { sanitizeString } from '../utils/security';
import { fetchServiceRecordDetail, fetchAppointmentsByServiceRecord } from '../services/serviceRecordsService';
import './ServisKaydiDetayModal.css';
import { getOnemBorderClass, getOnemRowClass, getPriorityToken } from '../utils/onemDerecesiHelper';

const safeText = (value) => sanitizeString(value ?? '');

const getDeviceName = (device = {}) => {
  return (
    device.urun?.icerikAdi ||
    device.urun?.urunAdi ||
    device.icerik?.icerikAdi ||
    device.icerik?.urunAdi ||
    device.urunAdi ||
    device.icerikAdi ||
    device.cihazAdi ||
    device.ad ||
    '---'
  );
};

const getDeviceSerial = (device = {}) => {
  return device.seriNumarasi || device.seriNo || device.serial || '---';
};

const formatDateTime = (dateValue) => {
  if (!dateValue) return '—';
  try {
    let date;
    if (dateValue && typeof dateValue === 'object' && dateValue.$date) {
      date = new Date(dateValue.$date);
    } else if (typeof dateValue === 'string') {
      date = new Date(dateValue);
    } else if (typeof dateValue === 'number') {
      date = new Date(dateValue);
    } else if (dateValue instanceof Date) {
      date = dateValue;
    } else {
      return '—';
    }
    if (!date || isNaN(date.getTime())) return '—';
    return date.toLocaleString('tr-TR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (e) {
    return '—';
  }
};

const ServisKaydiDetayModal = ({ servisKaydiId, kaynakRandevuId, onClose }) => {
  const navigate = useNavigate();
  const [serviceRecord, setServiceRecord] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadData = useCallback(async () => {
    if (!servisKaydiId) return;
    setLoading(true);
    setError('');
    try {
      const [srRes, aptRes] = await Promise.all([
        fetchServiceRecordDetail(servisKaydiId),
        fetchAppointmentsByServiceRecord(servisKaydiId, { aktif: undefined }) // tüm randevular
      ]);
      const sr = srRes?.veri?.servisKaydi || srRes?.veri?.serviceRecord || srRes?.veri || null;
      const list = aptRes?.veri?.randevular || aptRes?.veri || [];
      setServiceRecord(sr);
      setAppointments(Array.isArray(list) ? list : []);
    } catch (err) {
      setError(err?.message || 'Servis kaydı yüklenirken hata oluştu');
      setServiceRecord(null);
      setAppointments([]);
    } finally {
      setLoading(false);
    }
  }, [servisKaydiId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleGoToAppointment = (randevuId) => {
    if (!servisKaydiId || !randevuId) return;
    onClose?.();
    navigate(`/service-records/${servisKaydiId}/appointments/${randevuId}`);
  };

  if (!servisKaydiId) return null;

  if (loading) {
    return (
      <div className="modal-overlay servis-kaydi-detay-modal" onClick={onClose}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2>Servis Kaydı</h2>
            <button type="button" className="modal-close-btn" onClick={onClose} aria-label="Kapat">
              <i className="fas fa-times"></i>
            </button>
          </div>
          <div className="modal-body loading-state">
            <div className="loading-spinner"></div>
            <p>Yükleniyor...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="modal-overlay servis-kaydi-detay-modal" onClick={onClose}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2>Servis Kaydı</h2>
            <button type="button" className="modal-close-btn" onClick={onClose} aria-label="Kapat">
              <i className="fas fa-times"></i>
            </button>
          </div>
          <div className="modal-body error-state">
            {safeText(error)}
          </div>
        </div>
      </div>
    );
  }

  const customer = serviceRecord?.musteri || {};
  const customerName = `${customer.adi || customer.name || ''} ${customer.soyadi || customer.surname || ''}`.trim() || '---';
  const customerPhone = customer.telefon || customer.phone || customer.gsm || '---';
  const priorityToken = getPriorityToken(serviceRecord?.onemDerecesi);
  const onemBorderClass = getOnemBorderClass(priorityToken);
  const selectedDevices =
    (Array.isArray(serviceRecord?.cihazlar) && serviceRecord.cihazlar) ||
    (Array.isArray(serviceRecord?.cihazKayitlari) && serviceRecord.cihazKayitlari) ||
    (Array.isArray(serviceRecord?.cihazBilgileri) && serviceRecord.cihazBilgileri) ||
    [];
  const deviceNotSelected = Boolean(serviceRecord?.cihazSecilmedi);
  const deviceNote = serviceRecord?.cihazAciklama || serviceRecord?.cihazNotu || '';
  const arizaList = (() => {
    if (Array.isArray(serviceRecord?.arizaKayitlari) && serviceRecord.arizaKayitlari.length > 0) {
      return serviceRecord.arizaKayitlari;
    }
    if (Array.isArray(serviceRecord?.arizalar) && serviceRecord.arizalar.length > 0) {
      return serviceRecord.arizalar;
    }
    const arizaIds = serviceRecord?.arizaIds;
    if (Array.isArray(arizaIds) && arizaIds.length > 0) {
      return arizaIds.map(a => {
        const obj = a && typeof a === 'object' ? a : { _id: a };
        const ad = obj.icerikAdi || obj.arizaAdi || obj.urunAdi || obj.ad || '';
        return { id: obj._id, icerikAdi: ad, arizaAdi: ad, ad };
      });
    }
    return [];
  })();
  const arizaNote = serviceRecord?.arizaNotu || '';
  const address = serviceRecord?.adres || {};
  const districtName = address.district?.name || address.district?.ad || '---';
  const cityName = address.city?.name || address.city?.ad || '---';
  const statusToken = (() => {
    const value = String(serviceRecord?.durum || '').toLowerCase();
    if (value.includes('devam')) return 'progress';
    if (value.includes('tamam') || value.includes('bitti')) return 'done';
    if (value.includes('iptal')) return 'cancel';
    return 'new';
  })();

  return (
    <div className="modal-overlay servis-kaydi-detay-modal" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Servis Kaydı - {customerName}</h2>
          <button type="button" className="modal-close-btn" onClick={onClose} aria-label="Kapat">
            <i className="fas fa-times"></i>
          </button>
        </div>
        <div className="modal-body">
          <div className={`servis-kaydi-info${onemBorderClass ? ` ${onemBorderClass}` : ''}`}>
            <div className="info-row">
              <div className="info-item">
                <FieldLabel className="info-label" as="span">Müşteri</FieldLabel>
                <span className="info-value">{customerName || '---'}</span>
                <span className="info-meta">Telefon: {customerPhone}</span>
              </div>
              <div className="info-item">
                <FieldLabel className="info-label" as="span">İşlem Tipi</FieldLabel>
                <span className="info-value">{serviceRecord?.islemTipi || '---'}</span>
              </div>
              <div className="info-item">
                <FieldLabel className="info-label" as="span">Önem Derecesi</FieldLabel>
                <span className={`priority-badge priority-${priorityToken}`}>
                  {serviceRecord?.onemDerecesi || '---'}
                </span>
              </div>
              <div className="info-item">
                <FieldLabel className="info-label" as="span">Durum</FieldLabel>
                <span className={`status-chip status-${statusToken}`}>
                  {serviceRecord?.durum || '---'}
                </span>
              </div>
            </div>

            <div className="info-section">
              <FieldLabel className="info-section__label" as="div">Adres</FieldLabel>
              <div className="info-section__value">{address.adres || '---'}</div>
              <div className="info-section__meta">{districtName} / {cityName}</div>
            </div>

            <div className="info-section">
              <FieldLabel className="info-section__label" as="div">Seçilen Cihazlar</FieldLabel>
              {deviceNotSelected ? (
                <div className="info-section__value">Cihaz seçilmedi</div>
              ) : selectedDevices.length > 0 ? (
                <div className="info-section__list">
                  {selectedDevices.map((device, index) => (
                    <div key={device.id || device._id || device.seriNumarasi || index} className="info-section__list-item">
                      <span>{getDeviceName(device)}</span>
                      <span className="info-section__meta">Seri No: {getDeviceSerial(device)}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="info-section__value">---</div>
              )}
              {deviceNote && (
                <div className="info-section__meta" style={{ marginTop: '8px' }}>
                  <strong>Cihaz Notu:</strong> {deviceNote}
                </div>
              )}
            </div>

            <div className="info-section">
              <FieldLabel className="info-section__label" as="div">Seçili Arızalar</FieldLabel>
              {arizaList.length > 0 ? (
                <div className="info-section__tags">
                  {arizaList.map((ariza) => (
                    <span key={ariza.id || ariza._id} className="tag tag--danger">
                      {ariza.icerikAdi || ariza.arizaAdi || ariza.ad || '---'}
                    </span>
                  ))}
                </div>
              ) : (
                <div className="info-section__value">---</div>
              )}
            </div>

            {arizaNote && (
              <div className="info-section">
                <FieldLabel className="info-section__label" as="div">Arıza Notu</FieldLabel>
                <div className="info-section__value">{arizaNote}</div>
              </div>
            )}

            {serviceRecord?.notlar && (
              <div className="info-section">
                <FieldLabel className="info-section__label" as="div">Notlar</FieldLabel>
                <div className="info-section__value">{serviceRecord.notlar}</div>
              </div>
            )}
          </div>

          <div className="servis-kaydi-randevular">
            <div className="servis-kaydi-randevular-title">
              <i className="fas fa-calendar-alt"></i>
              <span>Bu Servis Kaydına Ait Randevular</span>
            </div>
            {appointments.length === 0 ? (
              <p className="servis-kaydi-randevu-empty">Randevu bulunamadı</p>
            ) : (
              <table className="admins-table servis-kaydi-randevu-table">
                <thead>
                  <tr>
                    <th>Randevu No</th>
                    <th>Tarih</th>
                    <th>Durum</th>
                    <th className="islemler-sutunu">İşlem</th>
                  </tr>
                </thead>
                <tbody>
                  {appointments.map((apt) => {
                    const rid = apt.id || apt._id;
                    const ridStr = rid ? String(rid) : '';
                    const rno = apt.randevuNumarasi || rid || '—';
                    const rtarih = apt.randevuTarihi || apt.tamamlanmaBilgileri?.tamamlanmaTarihi;
                    const isKaynak = kaynakRandevuId && ridStr === String(kaynakRandevuId);
                    return (
                      <tr
                        key={rid}
                        className={[
                          getOnemRowClass(serviceRecord?.onemDerecesi),
                          isKaynak ? 'randevu-satir-kaynak' : '',
                        ].filter(Boolean).join(' ')}
                      >
                        <td>{rno}</td>
                        <td>{formatDateTime(rtarih)}</td>
                        <td>{apt.durum || '—'}</td>
                        <td className="islemler-sutunu">
                          <button
                            type="button"
                            className="randevuya-git-link"
                            onClick={() => handleGoToAppointment(rid)}
                            title="Randevu detay sayfasına git"
                          >
                            <i className="fas fa-external-link-alt"></i>
                            <span>Randevuya Git</span>
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={onClose}>Kapat</button>
        </div>
      </div>
    </div>
  );
};

export default ServisKaydiDetayModal;
